import {
  Box,
  Button,
  HStack,
  Input,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import TrackingPageLayout from "components/TrackingPageLayout";
import { useAuthentication } from "hooks/useAuthentication";
import { useGuardDailyReport, exportGuardDailyReportFile } from "hooks/useGuard";
import { showToast, toastTypes } from "utils";

export default function TrackingGuardDailyReportPage() {
  const toast = useToast();
  const { user } = useAuthentication();
  const [date, setDate] = useState<string | undefined>(undefined);
  const { report, isLoading, error, refetch } = useGuardDailyReport(date);
  const [exporting, setExporting] = useState<"pdf" | "xlsx" | null>(null);

  const handleExport = async (format: "pdf" | "xlsx") => {
    setExporting(format);
    try {
      const blob = await exportGuardDailyReportFile(user?.auth?.access_token, {
        date,
        format,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = format === "pdf" ? "rapport_guard.pdf" : "rapport_guard.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur lors de l'export du rapport.";
      showToast({ toast, message: msg, status: toastTypes.error });
    } finally {
      setExporting(null);
    }
  };

  return (
    <TrackingPageLayout
      title="Rapport quotidien Guard"
      action={
        <HStack spacing={3} align="center">
          <Input
            type="date"
            max={new Date().toISOString().slice(0, 10)}
            value={date ?? ""}
            onChange={(e) => setDate(e.target.value || undefined)}
            w="200px"
            size="sm"
          />
          <Button size="sm" onClick={() => refetch()} isLoading={isLoading}>
            Charger
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleExport("pdf")}
            isLoading={exporting === "pdf"}
          >
            Export PDF
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleExport("xlsx")}
            isLoading={exporting === "xlsx"}
          >
            Export Excel
          </Button>
        </HStack>
      }
    >
      {error && (
        <Text color="red.600" mb={3}>
          Erreur lors du chargement du rapport.
        </Text>
      )}

      {isLoading ? (
        <Spinner />
      ) : (
        <Box p={4} borderWidth="1px" borderRadius="md" bg="white" fontSize="sm">
          {Object.keys(report).length === 0 ? (
            <Text color="gray.600">Aucune donnée pour cette date.</Text>
          ) : (
            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {JSON.stringify(report, null, 2)}
            </pre>
          )}
        </Box>
      )}
    </TrackingPageLayout>
  );
}

